/**
 * Authenticated fetch wrapper that handles 401 errors
 * with automatic logout and redirect to login page.
 */

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3520/api'

// Force redirect to login and clear auth data
const handleAuthError = () => {
  console.log('[authFetch] 401 detected - clearing auth and redirecting')
  localStorage.removeItem('admin_access_token')
  localStorage.removeItem('admin_user')
  localStorage.removeItem('admin_user_role')

  // Hard redirect to login
  if (typeof window !== 'undefined' && !window.location.pathname.includes('/login')) {
    window.location.href = '/login'
  }
}

/**
 * Fetch wrapper that automatically handles 401 errors
 * @param input - URL or Request object
 * @param init - Fetch init options
 * @returns Response object
 * @throws Error if session expired or other error
 */
export const authFetch = async (
  input: RequestInfo | URL,
  init?: RequestInit
): Promise<Response> => {
  const token = localStorage.getItem('admin_access_token')

  // Add authorization header if token exists
  const headers: HeadersInit = {
    ...init?.headers,
  }

  if (token) {
    ;(headers as Record<string, string>)['Authorization'] = `Bearer ${token}`
  }

  const response = await fetch(input, {
    ...init,
    headers,
  })

  // Check for 401 and handle it immediately
  if (response.status === 401) {
    handleAuthError()
    throw new Error('Session expired. Please login again.')
  }

  return response
}

/**
 * Helper to make API calls with auth and proper error handling
 */
export const apiCall = async <T>(
  endpoint: string,
  options?: RequestInit
): Promise<T> => {
  const url = endpoint.startsWith('http') ? endpoint : `${API_URL}${endpoint}`
  const response = await authFetch(url, {
    headers: {
      'Content-Type': 'application/json',
    },
    ...options,
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({}))
    throw new Error(error.message || `Request failed with status ${response.status}`)
  }

  return response.json()
}
